/* eslint-disable react-hooks/exhaustive-deps */
import MonthPicker from "./MonthPicker";
import { Link } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { HiChevronRight } from "react-icons/hi";
import { getType } from "./helper";
import MyAdminComponent from "./MyAdminComponent";
import { getPageRouteFromKey } from "./routesHelper";
import { pushLevel, setAdminOption } from "../../lib";
import { useStaticContext } from "../../context/StaticContext";
import { addOneLevel } from "../../lib/breadCrumb/BreadCrumb";
import { useAdminContext } from "../../context/admin/AdminContext";
import { _PAGE_SIZE_ } from "../../lib/other/tools";
import { useAdminsContext } from "../../context/admin/AdminManageContext";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const formatLabel = (key: string) =>
  String(key || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());

const AllAdminsStats = () => {
  const { getStats, stats } = useStaticContext();
  const { sysAdmin, setSingleAdmin } = useAdminContext();
  const { admins, getAllByAdminCompanyId, count } = useAdminsContext();

  const [currentAdmin, setCurrentAdmin] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(_PAGE_SIZE_);

  const [localStatistics, setLocalSetStats] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [total, setTotal] = useState(0);

  useEffect(() => {
    if (!admins) getAllByAdminCompanyId(page, pageSize);

    if (!stats && admins && admins?.length > 0) {
      loadStatistics({ year: currentYear, month: currentMonth });
    } else if (stats && admins && admins?.length > 0) {
      setLocalSetStats(buildArrayOfKeysAndValuesFromAdminObjectData(stats));
    }
  }, [stats, admins]);

  const buildArrayOfKeysAndValuesFromAdminObjectData = (obj) => {
    const result = [];
    let total_items = 0;

    for (const key in obj) {
      if (!["email", "admin_id", "last_name", "first_name", "phonenumber"].includes(String(key))) {
        total_items += obj[key];
        result.push({
          title: key,
          value: obj[key],
          route: getPageRouteFromKey({ key }),
        });
      }
    }
    setTotal(total_items);
    return result;
  };

  const loadStatistics = async ({ year, month, admin }: any) => {
    if (admins && admins?.length > 0) {
      await getStats({
        year,
        month,
        data: { ids: admin ? [admin] : [admins[currentAdmin]?.admin_id] },
      });
    }
  };

  const activeAdmin = admins?.[currentAdmin];
  const adminFullName = activeAdmin
    ? `${String(activeAdmin?.first_name || "")} ${String(activeAdmin?.last_name || "")}`.trim()
    : "";
  const monthLabel = MONTH_NAMES[currentMonth - 1] ?? "";

  return (
    <div className="h-full overflow-y-auto bg-gray-50">
      <div className="mx-auto max-w-screen-2xl px-4 py-6 lg:px-8 lg:py-8">

        {/* ── Page header ──────────────────────────────────────── */}
        <header className="mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-brand">
              Admin statistics
            </p>
            <h1 className="mt-1 truncate text-2xl font-semibold tracking-tight text-gray-900">
              {adminFullName || "My statistics"}
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              Activity overview for {monthLabel} {currentYear}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <SummaryPill label="Total items" value={total} />
            <SummaryPill
              label="Period"
              value={`${String(currentMonth).padStart(2, "0")}/${currentYear}`}
            />
          </div>
        </header>

        {/* ── Period filter ───────────────────────────────────── */}
        <section className="mb-6 rounded-2xl border border-gray-200 bg-white shadow-sm">
          <MonthPicker
            currentYear={currentYear}
            currentMonth={currentMonth}
            setTotal={setTotal}
            setCurrentYear={setCurrentYear}
            loadStatistics={loadStatistics}
            setCurrentMonth={setCurrentMonth}
            setLocalSetStats={setLocalSetStats}
          />
        </section>

        {/* ── Main: admins + stats ────────────────────────────── */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">

          {/* Admins sidebar */}
          <aside className="lg:col-span-4 xl:col-span-3">
            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
              <header className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
                <h2 className="text-sm font-semibold text-gray-900">Team members</h2>
                <span className="text-[11px] font-medium text-gray-500">
                  {typeof count === "number" ? `${count} total` : `${admins?.length || 0} shown`}
                </span>
              </header>
              <div className="p-3">
                <MyAdminComponent
                  page={page}
                  count={count}
                  admins={admins}
                  pageSize={pageSize}
                  currentYear={currentYear}
                  currentAdmin={currentAdmin}
                  currentMonth={currentMonth}
                  getAllByAdminCompanyId={getAllByAdminCompanyId}
                  setPage={setPage}
                  setTotal={setTotal}
                  setPageSize={setPageSize}
                  loadStatistics={loadStatistics}
                  setCurrentAdmin={setCurrentAdmin}
                  setLocalSetStats={setLocalSetStats}
                />
              </div>
            </div>
          </aside>

          {/* Stats grid */}
          <section className="lg:col-span-8 xl:col-span-9">
            <div className="mb-4 flex items-end justify-between gap-3">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">Content overview</h2>
                <p className="mt-0.5 text-[11px] text-gray-500">
                  Items contributed by content type
                </p>
              </div>
              <span className="hidden sm:inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-[11px] font-medium text-gray-600">
                <span className="h-1.5 w-1.5 rounded-full bg-brand" />
                {monthLabel} {currentYear}
              </span>
            </div>

            {localStatistics ? (
              localStatistics.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-12 text-center">
                  <p className="text-sm font-semibold text-gray-900">No activity yet</p>
                  <p className="mt-1 text-xs text-gray-500">
                    Nothing recorded for this admin during {monthLabel} {currentYear}.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
                  {localStatistics.map((stat, index) => {
                    const label = formatLabel(stat?.title);
                    const value = Number(stat?.value) || 0;
                    const share =
                      total > 0 ? Math.min(100, Math.round((value / total) * 100)) : 0;

                    return (
                      <Link
                        key={`${label}-${index}`}
                        to={stat?.route}
                        onClick={() => {
                          setSingleAdmin({
                            ids: [admins?.[currentAdmin]?.admin_id],
                            year: currentYear,
                            month: currentMonth,
                            noLocation: true,
                            type: getType({ field: stat?.title }),
                            route: "/content/admin/stats",
                          });

                          addOneLevel({
                            id: 0,
                            name: `${admins?.[currentAdmin]?.first_name} - ${admins?.[currentAdmin]?.last_name}`,
                            route: "/levels/first",
                            level: "",
                            country_id: 0,
                            data: {
                              data: {
                                name: "",
                                country_id: 0,
                                first_level_id: 0,
                                second_level_id: 0,
                                third_level_id: 0,
                                fourth_level_id: 0,
                                level: "",
                              },
                            },
                          });

                          pushLevel({
                            level: {
                              id: 0,
                              name: "",
                              level: "All",
                              route: "/levels/first",
                              data: null,
                            },
                          });

                          setAdminOption(sysAdmin?.admin_id);
                        }}
                        className="group block rounded-2xl border border-gray-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <p className="min-w-0 truncate text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                            {label}
                          </p>
                          <HiChevronRight
                            className="h-4 w-4 shrink-0 text-gray-300 transition group-hover:translate-x-0.5 group-hover:text-gray-900"
                            aria-hidden
                          />
                        </div>
                        <p className="mt-3 text-3xl font-semibold tracking-tight text-gray-900 tabular-nums">
                          {value}
                        </p>
                        <div className="mt-4">
                          <div className="h-1 w-full overflow-hidden rounded-full bg-gray-100">
                            <div
                              className="h-full rounded-full bg-brand transition-all duration-300"
                              style={{ width: `${share}%` }}
                            />
                          </div>
                          <p className="mt-1.5 text-[11px] text-gray-500">
                            {share}% of total
                          </p>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div
                    key={i}
                    className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm"
                  >
                    <div className="h-3 w-32 animate-pulse rounded bg-gray-200" />
                    <div className="mt-4 h-8 w-20 animate-pulse rounded bg-gray-200" />
                    <div className="mt-5 h-1 w-full animate-pulse rounded bg-gray-100" />
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

const SummaryPill = ({ label, value }: { label: string; value: any }) => (
  <span className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-[11px] font-medium text-gray-600 shadow-sm">
    {label}
    <span className="font-semibold text-gray-900 tabular-nums">{value}</span>
  </span>
);

export default AllAdminsStats;
